// apps/client/src/hooks/pipelineTracker/usePipelineTargets.ts
//
// Pipeline Tracker: reading and editing targets and monthly inputs.
//
// ONE HOOK, BOTH DOCUMENTS, ONE FETCH PER PAGE. The Deal movements tiles, the 12-month trend and
// the editor all read the same targets; loading them once in the tab and passing them down means
// a save in the editor recolours the tiles above it immediately, with no second copy to go stale.
//
// Same contract as the broker roster: the server takes the WHOLE document plus the version this
// page loaded, so two administrators saving at once get a refusal and a reload, never a silent
// overwrite. See useBrokerRoster.ts.

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { TAXONOMY_CALLABLE } from './reportContract';
import {
  GET_MONTHLY_INPUTS_ACTION,
  GET_TARGETS_ACTION,
  SAVE_MONTHLY_INPUTS_ACTION,
  SAVE_TARGETS_ACTION,
  type MonthlyInputsDoc,
  type TargetsDoc,
} from './targetsContract';
import { invalidateTrackerReports } from './reportCache';

export interface TargetsSaveOutcome {
  ok: boolean;
  conflict?: boolean;
  message?: string;
}

/**
 * What the editor saves. The two scalar targets take null as "clear": the callable wire turns an
 * undefined property into null, so null is the only explicit empty a scalar has (a map's is {}).
 * A field left undefined is not sent at all, and the server keeps the stored value.
 */
export type TargetsInput = Pick<TargetsDoc, 'stages' | 'byBroker' | 'rag' | 'sla' | 'monthlyInputTargets' | 'byPartner'> & {
  averageDealCents?: number | null;
  netSettlementsCents?: number | null;
};

export interface UsePipelineTargetsReturn {
  targets: TargetsDoc | null;
  /** True when nothing has been saved: every tile reads "No target set". */
  targetsIsSeed: boolean;
  monthlyInputs: MonthlyInputsDoc | null;
  loading: boolean;
  error: string | null;
  saving: boolean;
  saveTargets: (next: TargetsInput) => Promise<TargetsSaveOutcome>;
  saveMonthlyInputs: (months: MonthlyInputsDoc['months']) => Promise<TargetsSaveOutcome>;
  reload: () => Promise<void>;
}

const conflictOrFailure = (err: unknown, what: string): TargetsSaveOutcome => {
  const code = (err as { code?: string }).code ?? '';
  if (code.endsWith('aborted')) {
    return {
      ok: false,
      conflict: true,
      message: `Someone else changed the ${what} while this page was open. Reloaded; check them and try again.`,
    };
  }
  if (code.endsWith('invalid-argument')) {
    return { ok: false, message: `Those ${what} were refused: ${(err as Error).message}` };
  }
  return { ok: false, message: `The ${what} could not be saved. Nothing was changed.` };
};

/**
 * @param companyId tenant whose settings are read.
 * @param enabled   false makes no call at all (no company selected, or a viewer the callable would
 *                  refuse), rather than one that will be denied.
 */
export function usePipelineTargets(companyId: string | undefined, enabled = true): UsePipelineTargetsReturn {
  const [targets, setTargets] = useState<TargetsDoc | null>(null);
  const [targetsIsSeed, setTargetsIsSeed] = useState(true);
  const [monthlyInputs, setMonthlyInputs] = useState<MonthlyInputsDoc | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    if (!companyId || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      const call = httpsCallable(functions, TAXONOMY_CALLABLE);
      const [t, m] = (await Promise.all([
        call({ action: GET_TARGETS_ACTION, companyId }),
        call({ action: GET_MONTHLY_INPUTS_ACTION, companyId }),
      ])) as [
        HttpsCallableResult<{ targets: TargetsDoc; isSeed: boolean }>,
        HttpsCallableResult<{ monthlyInputs: MonthlyInputsDoc; isSeed: boolean }>,
      ];
      setTargets(t.data.targets);
      setTargetsIsSeed(t.data.isSeed);
      setMonthlyInputs(m.data.monthlyInputs);
    } catch (err) {
      // Loud, and the tiles fall back to "No target set" rather than a guessed figure: a target
      // that failed to load is not a target of zero.
      console.error('[PipelineTracker] targets load failed', err);
      setError('Targets could not be loaded, so no tile is graded against one right now.');
    } finally {
      setLoading(false);
    }
  }, [companyId, enabled]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const saveTargets = useCallback(
    async (next: TargetsInput): Promise<TargetsSaveOutcome> => {
      if (!companyId || !targets) return { ok: false, message: 'Not ready.' };
      setSaving(true);
      try {
        const call = httpsCallable(functions, TAXONOMY_CALLABLE);
        const res = (await call({
          action: SAVE_TARGETS_ACTION,
          companyId,
          stages: next.stages,
          ...(next.byBroker !== undefined ? { byBroker: next.byBroker } : {}),
          rag: next.rag,
          sla: next.sla,
          ...(next.monthlyInputTargets !== undefined ? { monthlyInputTargets: next.monthlyInputTargets } : {}),
          ...(next.averageDealCents !== undefined ? { averageDealCents: next.averageDealCents } : {}),
          ...(next.netSettlementsCents !== undefined ? { netSettlementsCents: next.netSettlementsCents } : {}),
          ...(next.byPartner !== undefined ? { byPartner: next.byPartner } : {}),
          expectedVersion: targets.version,
        })) as HttpsCallableResult<{ version: number }>;
        // Every field set explicitly, not spread: `next` omits byBroker when the last override was
        // removed, and a spread would keep the old overrides grading the tiles until a reload.
        setTargets({
          ...targets,
          stages: next.stages,
          byBroker: next.byBroker,
          rag: next.rag,
          sla: next.sla,
          monthlyInputTargets: next.monthlyInputTargets,
          // Null (cleared) reads back as absent, as the server stores it. A field the editor did not
          // send keeps the stored value, exactly as the server carries it over.
          averageDealCents: next.averageDealCents === undefined ? targets.averageDealCents : next.averageDealCents ?? undefined,
          netSettlementsCents:
            next.netSettlementsCents === undefined ? targets.netSettlementsCents : next.netSettlementsCents ?? undefined,
          byPartner: next.byPartner === undefined ? targets.byPartner : Object.keys(next.byPartner).length > 0 ? next.byPartner : undefined,
          version: res.data.version,
        });
        setTargetsIsSeed(false);
        invalidateTrackerReports();
        return { ok: true };
      } catch (err) {
        console.error('[PipelineTracker] saveTargets failed', err);
        const outcome = conflictOrFailure(err, 'targets');
        if (outcome.conflict) void reload();
        return outcome;
      } finally {
        setSaving(false);
      }
    },
    [companyId, targets, reload]
  );

  const saveMonthlyInputs = useCallback(
    async (months: MonthlyInputsDoc['months']): Promise<TargetsSaveOutcome> => {
      if (!companyId || !monthlyInputs) return { ok: false, message: 'Not ready.' };
      setSaving(true);
      try {
        const call = httpsCallable(functions, TAXONOMY_CALLABLE);
        const res = (await call({
          action: SAVE_MONTHLY_INPUTS_ACTION,
          companyId,
          months,
          expectedVersion: monthlyInputs.version,
        })) as HttpsCallableResult<{ version: number }>;
        setMonthlyInputs({ ...monthlyInputs, months, version: res.data.version });
        invalidateTrackerReports();
        return { ok: true };
      } catch (err) {
        console.error('[PipelineTracker] saveMonthlyInputs failed', err);
        const outcome = conflictOrFailure(err, 'monthly inputs');
        if (outcome.conflict) void reload();
        return outcome;
      } finally {
        setSaving(false);
      }
    },
    [companyId, monthlyInputs, reload]
  );

  return {
    targets,
    targetsIsSeed,
    monthlyInputs,
    loading,
    error,
    saving,
    saveTargets,
    saveMonthlyInputs,
    reload,
  };
}
